import React, { useState } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import PaymentOutlinedIcon from '@mui/icons-material/PaymentOutlined';
import { useThemeMode } from '../ThemeContext';
import { getStripeApiMessage, startSubscriberCheckout } from '../helpers/subscription_payment_helper';

function DetailRow({ label, value, mode, highlight = false }) {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: '16px',
      padding: '10px 0',
      borderBottom: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#eeeeee'}`,
    }}>
      <span style={{
        color: mode === 'dark' ? '#a0a0a0' : '#666666',
        fontSize: '14px',
        fontFamily: 'Urbanist, Arial, sans-serif',
      }}>
        {label}
      </span>
      <span style={{
        color: highlight ? '#26C3C0' : (mode === 'dark' ? '#FFFFFF' : '#333333'),
        fontSize: '14px',
        fontFamily: 'Urbanist, Arial, sans-serif',
        fontWeight: highlight ? '700' : '500',
        textAlign: 'right',
      }}>
        {value}
      </span>
    </div>
  );
}

function PendingPaymentModal({ isOpen, pendingPayment, onClose }) {
  const { mode } = useThemeMode();
  const [isPaying, setIsPaying] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  if (!isOpen || !pendingPayment) {
    return null;
  }

  const handleClickToPay = async () => {
    try {
      setIsPaying(true);
      setErrorMessage('');
      await startSubscriberCheckout(pendingPayment, { mode });
    } catch (error) {
      console.error('Error starting payment:', error);
      setErrorMessage(getStripeApiMessage(error, 'Failed to start payment. Please try again.'));
      setIsPaying(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
        padding: '16px',
      }}
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pending-payment-title"
        style={{
          width: '100%',
          maxWidth: '520px',
          background: mode === 'dark' ? '#141414' : '#ffffff',
          border: `1px solid ${mode === 'dark' ? '#333333' : '#e0e0e0'}`,
          borderRadius: '16px',
          boxShadow: mode === 'dark'
            ? '0 20px 48px rgba(0, 0, 0, 0.45)'
            : '0 20px 48px rgba(0, 0, 0, 0.12)',
          overflow: 'hidden',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: '12px',
          padding: '20px 20px 0',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '44px',
              height: '44px',
              borderRadius: '12px',
              background: 'rgba(255, 193, 7, 0.18)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}>
              <PaymentOutlinedIcon style={{ color: '#ffc107', fontSize: '24px' }} />
            </div>
            <div>
              <h2
                id="pending-payment-title"
                style={{
                  margin: 0,
                  color: mode === 'dark' ? '#FFFFFF' : '#333333',
                  fontSize: '20px',
                  fontFamily: 'Urbanist, Arial, sans-serif',
                  fontWeight: '600',
                }}
              >
                Payment Required
              </h2>
              <p style={{
                margin: '4px 0 0',
                color: mode === 'dark' ? '#a0a0a0' : '#666666',
                fontSize: '14px',
                fontFamily: 'Urbanist, Arial, sans-serif',
              }}>
                Complete your pending subscription payment to continue uninterrupted access.
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={isPaying}
            aria-label="Close"
            style={{
              background: 'transparent',
              border: 'none',
              color: mode === 'dark' ? '#bbbbbb' : '#666666',
              cursor: isPaying ? 'not-allowed' : 'pointer',
              padding: '4px',
              borderRadius: '6px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>

        <div style={{ padding: '20px' }}>
          <div style={{
            background: mode === 'dark' ? '#0f0f0f' : '#f8f9fa',
            border: `1px solid ${mode === 'dark' ? '#2a2a2a' : '#e8e8e8'}`,
            borderRadius: '12px',
            padding: '4px 16px',
            marginBottom: '16px',
          }}>
            <DetailRow label="Plan" value={pendingPayment.planName} mode={mode} />
            <DetailRow label="Billing Period" value={pendingPayment.date} mode={mode} />
            <DetailRow label="Amount" value={pendingPayment.amount} mode={mode} highlight />
            <DetailRow
              label="Status"
              value={(
                <span style={{
                  display: 'inline-block',
                  padding: '4px 10px',
                  borderRadius: '6px',
                  backgroundColor: mode === 'dark' ? 'rgba(255, 193, 7, 0.22)' : '#fff8e1',
                  color: mode === 'dark' ? '#ffc107' : '#b8860b',
                  border: `1px solid ${mode === 'dark' ? 'rgba(255, 193, 7, 0.45)' : '#ffe082'}`,
                  fontSize: '12px',
                  fontWeight: '600',
                  textTransform: 'capitalize',
                }}>
                  {pendingPayment.paymentStatus}
                </span>
              )}
              mode={mode}
            />
          </div>

          {errorMessage && (
            <div style={{
              marginBottom: '16px',
              padding: '10px 12px',
              borderRadius: '8px',
              background: mode === 'dark' ? 'rgba(244, 67, 54, 0.12)' : '#ffebee',
              color: mode === 'dark' ? '#ef9a9a' : '#c62828',
              fontSize: '13px',
              fontFamily: 'Urbanist, Arial, sans-serif',
            }}>
              {errorMessage}
            </div>
          )}

          <div style={{
            display: 'flex',
            gap: '12px',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}>
            <button
              type="button"
              onClick={onClose}
              disabled={isPaying}
              style={{
                background: 'transparent',
                border: `1px solid ${mode === 'dark' ? '#444444' : '#cccccc'}`,
                color: mode === 'dark' ? '#FFFFFF' : '#333333',
                padding: '10px 18px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                fontFamily: 'Urbanist, Arial, sans-serif',
                cursor: isPaying ? 'not-allowed' : 'pointer',
                minWidth: '100px',
              }}
            >
              Close
            </button>
            <button
              type="button"
              onClick={handleClickToPay}
              disabled={isPaying}
              style={{
                background: '#26C3C0',
                border: '1px solid #26C3C0',
                color: '#ffffff',
                padding: '10px 18px',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '600',
                fontFamily: 'Urbanist, Arial, sans-serif',
                cursor: isPaying ? 'wait' : 'pointer',
                minWidth: '130px',
                opacity: isPaying ? 0.8 : 1,
              }}
            >
              {isPaying ? 'Processing...' : 'Click to Pay'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PendingPaymentModal;
